import { useEffect, useState } from "react";
import { ChevronLeft } from "lucide-react";
import ImageAnalysisResults from "@/components/agent/vision/ImageAnalysisResults";
import ImageUploadWidget from "@/components/agent/vision/ImageUploadWidget";
import { Button } from "@/components/ui/button";
import { useBrain } from "@/contexts/ui/brain";
import { useSidebar } from "@/contexts/ui/sidebar";
import { SetupAction, SetupMode, SetupPhase } from "@/hooks/agent/useGenieSetupReducer";
import { useRole } from "@/hooks/auth";
import { useImageAttachments } from "@/hooks/voice/useImageAttachments";
import { useIsMobile } from "@/hooks/ui/useIsMobile";
import { useChatModalState } from "@/hooks/agent/useChatModalState";
import { useTextMessageHandler } from "@/hooks/agent/useTextMessageHandler";
import { SetupAnalysisResult } from "@/types/agent/setup";
import { ConfigurationPanel, LoadingScreen, SetupModeSelector, TestModeButton, TestModeModal, VoiceControlsSection } from "./components";

interface TalkSetupViewProps {
  speaking: boolean;
  listening: boolean;
  thinking: boolean;
  connected: boolean;
  isConnecting: boolean;
  setupPhase: SetupPhase;
  setupMode: SetupMode;
  analysisResult: SetupAnalysisResult | null;
  isAnalyzing: boolean;
  error: string | null;
  isResuming: boolean;
  messages: any[];
  handleConversationToggleWithWakeLock: () => Promise<void>;
  analyzeImage: (imageDataUrl: string) => Promise<void>;
  resumeSetup: () => Promise<void>;
  getStatusMessage: () => string;
  setSetupPhase: (phase: SetupPhase) => void;
  setSetupMode: (mode: SetupMode) => void;
  getService: () => any;
  dispatch: React.Dispatch<SetupAction>;
  voiceAgent: any;
}

export const TalkSetupView = ({
  speaking,
  listening,
  thinking,
  connected,
  isConnecting,
  setupPhase,
  setupMode,
  analysisResult,
  isAnalyzing,
  error,
  isResuming,
  messages,
  handleConversationToggleWithWakeLock,
  analyzeImage,
  resumeSetup,
  getStatusMessage,
  setSetupPhase,
  setSetupMode,
  getService,
  dispatch,
}: TalkSetupViewProps) => {
  const isMobile = useIsMobile();
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isAllAudioMuted, setIsAllAudioMuted] = useState(false);
  const [hasClosedSidebarForGuide, setHasClosedSidebarForGuide] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);
  const [showTestModeModal, setShowTestModeModal] = useState(false);

  const { setSidebarOpen } = useSidebar();
  const { openBrain } = useBrain();
  const { isInternalAdmin } = useRole();

  const {
    attachments,
    addFiles,
    removeAttachment,
    clearAll,
    consumeReadyContext,
  } = useImageAttachments();

  const chatModalState = useChatModalState();

  const { inlineMessage, setInlineMessage, handleInlineSend } = useTextMessageHandler({
    connected,
    isConnecting,
    getService,
    handleConversationToggleWithWakeLock,
    dispatch,
    attachments,
    consumeReadyContext,
    clearAttachments: clearAll,
  });

  useEffect(() => {
    if (guideVisible && setupPhase === "conversation" && !connected && !hasClosedSidebarForGuide) {
      setSidebarOpen(false);
      setHasClosedSidebarForGuide(true);
    }
  }, [guideVisible, setupPhase, connected, hasClosedSidebarForGuide, setSidebarOpen]);

  useEffect(() => {
    if (!connected) {
      setIsAllAudioMuted(false);
    }
  }, [connected]);

  const handleSendMessage = async () => {
    await handleInlineSend(
      chatModalState.openChatModal,
      chatModalState.setTextOnlyMode,
    );
  };

  const handleToggleAllAudioMute = async () => {
    const service = getService();
    if (!service) return;

    const shouldUnmuteAll = isAllAudioMuted;
    try {
      if (shouldUnmuteAll) {
        await service.setMicMuted(false);
        await service.setVolume(0.85);
        setIsAllAudioMuted(false);
      } else {
        await service.setMicMuted(true);
        await service.setVolume(0);
        setIsAllAudioMuted(true);
      }
    } catch (error) {
      console.error("Error toggling setup all-audio mute", error);
    }
  };

  const renderRightContent = () => {
    if (setupPhase === "analysis") {
      return (
        <div className="h-full flex items-center justify-center">
          <LoadingScreen message={getStatusMessage()} />
        </div>
      );
    }

    const configPhase = setupPhase as "conversation" | "analysis" | "confirmation" | "complete";
    return (
      <ConfigurationPanel
        analysisResult={analysisResult}
        messages={messages}
        isAnalyzing={isAnalyzing}
        interfaceMode={setupMode === "chat" ? "text" : "voice"}
        connected={connected}
        setupPhase={configPhase}
        onGuideVisibilityChange={setGuideVisible}
      />
    );
  };

  return (
    <>
      <TestModeButton
        visible={isInternalAdmin() && setupPhase !== "confirmation" && setupPhase !== "complete"}
        onClick={() => setShowTestModeModal(true)}
      />

      {setupPhase === "image-upload" ? (
        <div className="w-full h-full flex items-center justify-center p-4 sm:p-6">
          <ImageUploadWidget
            onImageSelect={(imageDataUrl) => {
              setUploadedImage(imageDataUrl);
              analyzeImage(imageDataUrl);
            }}
            onCancel={() => setSetupPhase("conversation")}
            isAnalyzing={isAnalyzing}
          />
        </div>
      ) : setupPhase === "image-results" && analysisResult && uploadedImage ? (
        <div className="w-full h-full overflow-y-auto bg-[#F9FAFC]">
          <ImageAnalysisResults
            analysis={analysisResult}
            uploadedImage={uploadedImage}
            onProceedToConfirmation={() => setSetupPhase("confirmation")}
            onRetry={() => {
              setUploadedImage(null);
              setSetupPhase("image-upload");
            }}
          />
        </div>
      ) : !setupMode ? (
        <SetupModeSelector
          onSelectVoice={() => setSetupMode("voice")}
          onSelectChat={() => {
            openBrain("Help me set up a genie");
          }}
          onUploadImage={() => setSetupPhase("image-upload")}
          onSelectListener={() => setSetupPhase("listener-setup")}
        />
      ) : (
        <div className="flex flex-col md:flex-row w-full h-full">
          {/* Left side - Voice setup (responsive width) */}
          <div className="w-full md:w-1/2 flex flex-col px-3 py-2 md:py-0 md:h-full relative pb-16 md:pb-0">
            {/* Exit button - desktop only */}
            <Button
              onClick={() => window.history.back()}
              variant="ghost"
              size="sm"
              className="hidden md:flex self-start mt-1 md:mt-2 text-gray-600 px-2 md:px-3 py-1 md:py-2 text-sm md:text-base min-w-0 h-8 md:h-9 items-center gap-1 md:gap-2 transition-colors duration-200 rounded-md hover:text-white hover:bg-gradient-to-r hover:from-[#4E9CFF] hover:to-[#FF4DB8]"
            >
              <ChevronLeft size={16} className="md:w-4 md:h-4" />
              <span className="hidden md:inline">Exit</span>
            </Button>

            <div className="flex-1 flex flex-col items-center justify-center py-4 md:py-0">
              {/* Mobile: Back button and status message on same line - hidden when in chat modal */}
              {!chatModalState.showChatModal && (
                <div className="md:hidden flex items-center w-full max-w-xs mb-2 sm:mb-3">
                  <Button
                    onClick={() => window.history.back()}
                    variant="ghost"
                    size="sm"
                    aria-label="Exit"
                    title="Exit"
                    className="text-gray-600 px-1 py-1 text-sm min-w-0 h-8 flex items-center gap-1 -ml-1 rounded-md hover:text-white hover:bg-gradient-to-r hover:from-[#4E9CFF] hover:to-[#FF4DB8]"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <p className="text-sm sm:text-base text-gray-600 text-center font-medium leading-relaxed flex-1 ml-1">
                    {getStatusMessage()}
                  </p>
                </div>
              )}

              {/* Desktop: Status message centered - hidden when in chat modal */}
              {!chatModalState.showChatModal && (
                <p className="hidden md:block text-base md:text-lg text-gray-600 mb-6 md:mb-8 text-center max-w-xs md:max-w-sm font-medium leading-relaxed">
                  {getStatusMessage()}
                </p>
              )}

              {/* Voice Controls Section */}
              <VoiceControlsSection
                isMobile={isMobile}
                showChatModal={chatModalState.showChatModal}
                speaking={speaking}
                listening={listening}
                thinking={thinking}
                connected={connected}
                isConnecting={isConnecting}
                messages={messages}
                inlineMessage={inlineMessage}
                onChangeInlineMessage={setInlineMessage}
                onSendMessage={handleSendMessage}
                onToggleConversation={handleConversationToggleWithWakeLock}
                attachments={attachments}
                onRemoveAttachment={removeAttachment}
                onOpenFilePicker={() => {
                  const input = document.getElementById(
                    "genie-setup-attachments",
                  ) as HTMLInputElement | null;
                  if (input) {
                    input.click();
                  }
                }}
                isTextOnlyMode={chatModalState.isTextOnlyMode}
                isAllAudioMuted={isAllAudioMuted}
                onToggleAllAudioMute={() => void handleToggleAllAudioMute()}
                startedFromText={chatModalState.startedFromText}
                onCloseOverlay={chatModalState.closeChatModal}
                onEndChat={() => {
                  chatModalState.closeChatModal();
                  handleConversationToggleWithWakeLock();
                }}
              />

              {/* Resume Button (when there's an error and not connected) */}
              {error && !connected && !isConnecting && (
                <Button
                  onClick={resumeSetup}
                  disabled={isResuming}
                  size="lg"
                  className="mt-3 sm:mt-4 md:mt-6 bg-orange-600 hover:bg-orange-700 text-white px-5 sm:px-6 md:px-8 py-2 md:py-3 text-sm md:text-base whitespace-nowrap"
                >
                  {isResuming ? "Resuming..." : "Resume Setup"}
                </Button>
              )}
            </div>
          </div>

          {/* Right side - Content (responsive width) */}
          <div className="w-full md:w-1/2 md:h-full">{renderRightContent()}</div>
        </div>
      )}

      <TestModeModal
        open={showTestModeModal}
        onOpenChange={setShowTestModeModal}
        onLoadConfiguration={setSetupPhase}
        dispatch={dispatch}
      />

      {/* Hidden file input for image attachments */}
      <input
        id="genie-setup-attachments"
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(event) => {
          addFiles(event.target.files);
          event.target.value = "";
        }}
      />
    </>
  );
};
